import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getInputStyle,
  getLabelStyle,
  getGradientTextStyle,
} from '../theme';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid email or password');
      }

      localStorage.setItem('auth_token', data.access_token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div style={{
      ...getCosmicBackground(),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.3), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
          radial-gradient(2px 2px at 130px 80px, rgba(157, 143, 219, 0.3), transparent),
          radial-gradient(1px 1px at 160px 120px, rgba(255, 255, 255, 0.3), transparent)
        `,
        backgroundSize: '200px 200px',
        pointerEvents: 'none',
        opacity: 0.6,
      }} />

      <div style={{
        ...getCardStyle('light'),
        width: '100%',
        maxWidth: '440px',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <Link to="/" style={{
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <span style={{
              fontSize: '36px',
              marginRight: '12px',
              color: colors.accent.gold,
              textShadow: `0 0 15px rgba(212, 175, 55, 0.5)`,
            }}>
              {'\u2605'}
            </span>
            <span style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              ...getGradientTextStyle('gold'),
            }}>
              StarDecision
            </span>
          </Link>

          <h1 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.background.midnight,
            margin: '0 0 8px 0',
            letterSpacing: typography.letterSpacing.wide,
          }}>
            Welcome Back
          </h1>
          <p style={{
            fontSize: typography.fontSize.base,
            color: colors.text.muted,
          }}>
            Sign in to access your cosmic timing guidance
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: colors.status.errorBg,
            color: colors.status.error,
            padding: '14px 16px',
            borderRadius: borderRadius.md,
            marginBottom: '20px',
            fontSize: typography.fontSize.sm,
            border: `1px solid rgba(248, 113, 113, 0.3)`,
          }}>
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={getLabelStyle('light')}>
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={getInputStyle('light')}
              placeholder="Enter your email"
            />
          </div>

          <div style={{ marginBottom: '28px' }}>
            <label style={getLabelStyle('light')}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              style={getInputStyle('light')}
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...getButtonStyle('primary'),
              width: '100%',
              padding: '16px',
              fontSize: typography.fontSize.base,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Forgot Password */}
        <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '28px' }}>
          <button
            type="button"
            onClick={() => alert('Password reset functionality coming soon!')}
            style={{
              color: colors.primary.violet,
              textDecoration: 'none',
              fontSize: typography.fontSize.sm,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              fontFamily: typography.fontFamily.body,
            }}
          >
            Forgot your password?
          </button>
        </div>

        {/* Sign Up Link */}
        <div style={{
          textAlign: 'center',
          paddingTop: '24px',
          borderTop: `1px solid rgba(107, 91, 189, 0.2)`,
        }}>
          <p style={{
            fontSize: typography.fontSize.sm,
            color: colors.text.muted,
            margin: '0 0 16px 0',
          }}>
            Don't have an account?{' '}
            <Link to="/register" style={{
              color: colors.primary.violet,
              textDecoration: 'none',
              fontWeight: typography.fontWeight.medium,
            }}>
              Sign up for free
            </Link>
          </p>
          <Link to="/" style={{
            color: colors.text.muted,
            textDecoration: 'none',
            fontSize: typography.fontSize.xs,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            {'\u2190'} Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
